import { styled, css } from '@mui/material/styles';
import { format } from 'date-fns';
import React from 'react';

import { getDesignTokens } from '~/client/components/base/core';
import { Drawer } from '~/client/components/base/drawer';
import { UserProfileCircle } from '~/client/components/base/icon';
import StatusPill from '~/client/components/base/status-pill';
import Text from '~/client/components/base/text';
import * as GQL from '~/graphql/generated/client';

interface Props {
  open: boolean;
  onClose: () => void;
  task: GQL.ReportTask | null;
  className?: string;
}

type Step = NonNullable<GQL.ReportTask['review']>;

/**
 * Each decider's stated basis for their decision, read-only.
 *
 * Mirrors TaskCommentDrawer, which does the same for assessment tasks. A
 * report has at most two entries — the reviewer's and the approver's, one per
 * step — and they are never editable, because the comment is part of the
 * sign-off record. On a rejection the entry is the instruction: what to fix
 * before regenerating.
 */
const ReportTaskCommentDrawerImpl: React.FC<Props> = ({
  open,
  onClose,
  task,
  className,
}) => {
  const formatDate = (date: Date | string | number | null | undefined) => {
    if (!date) {
      return '';
    }
    try {
      return format(new Date(date), 'MMM d, yyyy h:mm a');
    } catch {
      return '';
    }
  };

  const decided = task?.status !== GQL.AssignmentStatusType.Pending;

  // Only steps that have been decided have anything to say. Kept in workflow
  // order: the review came first, whatever the approver then wrote.
  const entries: { title: string; step: Step }[] = [];
  if (task?.review?.decision) {
    entries.push({ title: 'Review', step: task.review });
  }
  if (task?.approval?.decision) {
    entries.push({ title: 'Approval', step: task.approval });
  }

  const pillFor = (title: string, step: Step) =>
    step.decision === GQL.ReportVersionStepDecision.Rejected
      ? { tone: 'danger' as const, label: `${title} rejected` }
      : { tone: 'success' as const, label: `${title} approved` };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Review comments"
      width={480}
      className={className}
    >
      {!task ? null : (
        <div className="drawer-content">
          <div className="task-info">
            <Text sz="bold-body14">{task.templateName}</Text>
            <Text sz="body12" color="secondary">
              {`Version ${task.version} · ${task.projectName}`}
            </Text>
          </div>

          {entries.length > 0 ? (
            <div className="comment-list">
              {entries.map(({ title, step }) => {
                const pill = pillFor(title, step);
                const text = step.comment?.trim();
                return (
                  <div className="comment-item" key={title}>
                    <div className="user-info">
                      <div className="user-details">
                        <Text sz="bold-body14">
                          {step.decidedByName ?? step.assignedToName ?? title}
                        </Text>
                        {step.decidedAt && (
                          <Text sz="body12" color="textSecondary">
                            {formatDate(step.decidedAt)}
                          </Text>
                        )}
                      </div>
                      <UserProfileCircle />
                    </div>
                    <StatusPill tone={pill.tone} label={pill.label} />
                    <div className="comment-wrapper">
                      <Text sz="body14" className="comment-text">
                        {text
                          ? text
                          : 'No comment was recorded with this decision.'}
                      </Text>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-comments">
              <Text sz="body14" color="secondary">
                {decided
                  ? 'No comment was recorded with this decision.'
                  : // Pending is the ordinary case, not an absence: comments
                    // are written at the moment of each decision.
                    'This report is still awaiting a decision. The reviewer’s and approver’s comments appear here as each decides.'}
              </Text>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

const ReportTaskCommentDrawer = styled(ReportTaskCommentDrawerImpl)(({
  theme,
}) => {
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    .drawer-content {
      padding: ${theme.spacing(4)};
    }

    .task-info {
      margin-bottom: ${theme.spacing(3)};
      padding-bottom: ${theme.spacing(2)};
      border-bottom: 1px solid ${theme.palette.divider};
    }

    .comment-list {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(3)};
    }

    .comment-item {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(1.5)};
    }

    .user-info {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .user-details {
      display: flex;
      flex-direction: column;
    }

    .comment-wrapper {
      padding: ${theme.spacing(2)};
      border-radius: 8px;
      background: ${t.surface2};
    }

    .comment-text {
      white-space: pre-wrap;
      word-break: break-word;
    }

    .no-comments {
      padding: ${theme.spacing(3)} 0;
    }
  `;
});

export default ReportTaskCommentDrawer;
